/**
 * Draws one frame of the picture-in-picture "video tier" (phones / Safari,
 * where the floating window can only show a plain <video>). The caller feeds
 * `canvas.captureStream()` to that video and redraws every frame.
 *
 * Pure: colours arrive as resolved CSS strings (`accent` / `accentInk`) so this
 * module never reads CSS variables. Sizes scale with the canvas height so the
 * frame stays legible at small PiP sizes.
 */

export interface PipFrame {
  video: HTMLVideoElement | null;
  name: string;
  initials: string;
  /** e.g. "2 waiting"; null hides the top strip. */
  waitingLabel: string | null;
  accent: string;
  accentInk: string;
  /** Your own mic / camera, drawn as status icons (phones can't host buttons). */
  micOn: boolean;
  camOn: boolean;
}

export const PIP_CANVAS_SIZE = { width: 640, height: 360 } as const;

const BACKGROUND = "#14171f";
/** Badge fill for a muted mic / camera that is off. */
export const STATUS_OFF_FILL = "#e5484d";
const STATUS_ON_FILL = "rgba(0,0,0,0.6)";
const HAVE_CURRENT_DATA = 2;

/** Up to two uppercase letters from the first and last words; "?" if empty. */
export function initialsFor(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  const first = words[0][0];
  const last = words.length > 1 ? words[words.length - 1][0] : "";
  return (first + last).toUpperCase();
}

export function drawPipFrame(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  frame: PipFrame,
): void {
  ctx.fillStyle = BACKGROUND;
  ctx.fillRect(0, 0, width, height);

  const { video } = frame;
  if (video && video.readyState >= HAVE_CURRENT_DATA && video.videoWidth > 0) {
    // Cover-fit: scale to fill, crop the overflow evenly.
    const scale = Math.max(width / video.videoWidth, height / video.videoHeight);
    const dw = video.videoWidth * scale;
    const dh = video.videoHeight * scale;
    ctx.drawImage(video, (width - dw) / 2, (height - dh) / 2, dw, dh);
  } else {
    const radius = height * 0.22;
    ctx.fillStyle = frame.accent;
    ctx.beginPath();
    ctx.arc(width / 2, height / 2, radius, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = frame.accentInk;
    ctx.font = `600 ${Math.round(radius * 0.9)}px sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(frame.initials, width / 2, height / 2);
  }

  // Name pill, bottom-left.
  const fontSize = Math.round(height * 0.06);
  const pad = Math.round(height * 0.025);
  ctx.font = `500 ${fontSize}px sans-serif`;
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  const pillW = ctx.measureText(frame.name).width + pad * 2;
  const pillH = fontSize + pad * 2;
  const pillX = pad;
  const pillY = height - pad - pillH;
  ctx.fillStyle = "rgba(0,0,0,0.6)";
  ctx.beginPath();
  // roundRect is missing before Safari 16 / Chrome 99; a throw here would kill the caller's draw loop.
  if (typeof ctx.roundRect === "function") ctx.roundRect(pillX, pillY, pillW, pillH, pillH / 2);
  else ctx.rect(pillX, pillY, pillW, pillH);
  ctx.fill();
  ctx.fillStyle = "#fff";
  ctx.fillText(frame.name, pillX + pad, pillY + pillH / 2);

  // Waiting strip, top.
  if (frame.waitingLabel) {
    const stripH = Math.round(height * 0.11);
    ctx.fillStyle = frame.accent;
    ctx.fillRect(0, 0, width, stripH);
    ctx.fillStyle = frame.accentInk;
    ctx.font = `600 ${Math.round(stripH * 0.5)}px sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(frame.waitingLabel, width / 2, stripH / 2);
  }

  // Your mic and camera, bottom-right. Pictures only: the browser's floating
  // window can't host buttons, so this says whether people can hear and see you.
  const badge = Math.round(height * 0.1);
  const gap = Math.round(badge * 0.3);
  const y = height - pad - badge;
  drawStatusBadge(ctx, width - pad - badge, y, badge, frame.camOn, drawCameraGlyph);
  drawStatusBadge(ctx, width - pad - badge * 2 - gap, y, badge, frame.micOn, drawMicGlyph);
}

type Glyph = (ctx: CanvasRenderingContext2D, cx: number, cy: number, s: number) => void;

function drawStatusBadge(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  on: boolean,
  glyph: Glyph,
): void {
  const r = size / 2;
  const cx = x + r;
  const cy = y + r;
  ctx.fillStyle = on ? STATUS_ON_FILL : STATUS_OFF_FILL;
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "#fff";
  ctx.strokeStyle = "#fff";
  ctx.lineWidth = Math.max(1.5, size * 0.07);
  glyph(ctx, cx, cy, size * 0.5);
  if (!on) {
    // Diagonal slash: "off".
    ctx.beginPath();
    ctx.moveTo(cx - size * 0.25, cy - size * 0.25);
    ctx.lineTo(cx + size * 0.25, cy + size * 0.25);
    ctx.stroke();
  }
}

/** A microphone: capsule body plus a stand. `s` is the glyph's height. */
const drawMicGlyph: Glyph = (ctx, cx, cy, s) => {
  const w = s * 0.38;
  const h = s * 0.6;
  ctx.beginPath();
  if (typeof ctx.roundRect === "function") ctx.roundRect(cx - w / 2, cy - s / 2, w, h, w / 2);
  else ctx.rect(cx - w / 2, cy - s / 2, w, h);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(cx, cy - s / 2 + h * 0.55, w * 0.95, 0, Math.PI);
  ctx.moveTo(cx, cy + s * 0.3);
  ctx.lineTo(cx, cy + s / 2);
  ctx.stroke();
};

/** A video camera: body plus a lens triangle. */
const drawCameraGlyph: Glyph = (ctx, cx, cy, s) => {
  const w = s * 0.62;
  const h = s * 0.5;
  const left = cx - s / 2;
  ctx.beginPath();
  ctx.rect(left, cy - h / 2, w, h);
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(left + w, cy);
  ctx.lineTo(cx + s / 2, cy - h / 2);
  ctx.lineTo(cx + s / 2, cy + h / 2);
  ctx.closePath();
  ctx.fill();
};
